import React from 'react';
import { Link } from 'react-router-dom';
import type { ProjectActivityEvent, WorkStreamSummary } from '../client';
import { formatCostCompact } from '../utils/formatCost';
import { externalClientLabel } from './external-client-label';
import MemberName from '../components/MemberName';

interface ActivityFeedProps {
  teamId: string;
  events: ProjectActivityEvent[];
}

export function formatTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function workStreamHref(teamId: string, data: Record<string, any>): string {
  const base = `/console/team/${teamId}/work-streams/${data.workStreamId}`;
  return data.workStreamRunId ? `${base}?run=${data.workStreamRunId}` : base;
}

export function workStreamIdFromActivity(
  event: ProjectActivityEvent,
): number | null {
  const data = (event.data || {}) as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(data, 'workStreamId')) return null;
  const id = data.workStreamId;
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0
    ? id
    : null;
}

/**
 * Trailing "(… was archived)" clause for a card_moved row a board
 * operation caused rather than a person dragging the card. Empty for
 * direct moves, which carry no `reason`.
 */
function moveReason(data: Record<string, any>): string {
  switch (data.reason) {
    case 'column_archived':
      return ` (column "${data.reasonColumnName || ''}" was archived)`;
    case 'sprint_epic_removed':
      return ` (epic "${data.reasonEpicName || ''}" was taken off sprint ${
        data.reasonSprintName || ''
      })`;
    case 'sprint_deleted':
      return ` (sprint ${data.reasonSprintName || ''} was deleted)`;
    default:
      return '';
  }
}

/**
 * Elapsed run time, phrased to sit at the end of a sentence ("completed …
 * in 4 minutes"). Whole units only: the feed is a timeline, not a profiler,
 * and "in 4.3 minutes" reads like a measurement nobody asked for.
 */
export function formatRunDuration(ms?: number | null): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  const hourPart = `${hours} hour${hours === 1 ? '' : 's'}`;
  if (!restMinutes) return hourPart;
  return `${hourPart} ${restMinutes} minute${restMinutes === 1 ? '' : 's'}`;
}

/** Human sentence for one activity event; links where a target exists. */
export function eventText(
  teamId: string,
  event: ProjectActivityEvent,
  workStream?: WorkStreamSummary | null,
): React.ReactNode {
  const data = (event.data || {}) as Record<string, any>;
  const via = event.externalClient
    ? ` via ${externalClientLabel(event.externalClient)}`
    : '';
  const actor = `${event.actorName || 'Someone'}${via}`;
  switch (event.type) {
    case 'external_work_handoff_created':
      return (
        <>
          {event.actorName || 'Someone'} prepared this card for{' '}
          {data.agentName || `agent ${data.agentId}`} —{' '}
          <Link to={workStreamHref(teamId, data)}>view Work stream</Link>
        </>
      );
    case 'external_work_started':
      return (
        <>
          {data.agentName || `Agent ${data.agentId}`} started external work
          {data.client ? ` via ${externalClientLabel(data.client)}` : ''} —{' '}
          <Link to={workStreamHref(teamId, data)}>view Work stream</Link>
        </>
      );
    case 'external_work_expired':
      return (
        <>
          The handoff to {data.agentName || `agent ${data.agentId}`} expired
          before it was claimed —{' '}
          <Link to={workStreamHref(teamId, data)}>view Work stream</Link>
        </>
      );
    case 'external_work_finished':
      return (
        <>
          {event.actorName || 'Agent'} completed external work via{' '}
          {externalClientLabel(data.client)} —{' '}
          <Link to={workStreamHref(teamId, data)}>view Work stream</Link>
        </>
      );
    case 'project_created':
      return `${actor} created the project`;
    case 'project_updated':
      return `${actor} updated the project`;
    case 'card_created':
      return `${actor} created the card${data.title ? ` "${data.title}"` : ''}`;
    case 'card_updated':
      return `${actor} updated the card`;
    case 'card_moved': {
      // Legacy rows predate fromEpicId/Name; keep their original phrasing
      if (data.fromEpicName === undefined && data.fromEpicId === undefined) {
        return `${actor} moved the card${
          data.fromColumnName ? ` from ${data.fromColumnName}` : ''
        }${data.toColumnName ? ` to ${data.toColumnName}` : ''}${
          data.toSprintName && data.fromSprintName !== data.toSprintName
            ? ` (${data.toSprintName === 'Backlog' ? 'backlog' : `sprint ${data.toSprintName}`})`
            : ''
        }`;
      }
      // Board admin moves cards as a side effect (a column archived, an
      // epic taken off a sprint, a sprint deleted). Say what did it, so
      // the line doesn't read as someone dragging the card by hand.
      const because = moveReason(data);
      // Columns are per-epic: an epic move rematches the card into the
      // target epic's same-named column, changing the id but not the
      // status — so "did the column change" compares names, not ids
      const columnChanged =
        (data.fromColumnName ?? null) !== (data.toColumnName ?? null);
      const epicChanged = data.fromEpicId !== data.toEpicId;
      const sprintChanged = data.fromSprintId !== data.toSprintId;
      const sprint = sprintChanged
        ? ` (${data.toSprintName === 'Backlog' ? 'backlog' : `sprint ${data.toSprintName}`})`
        : '';
      // Backlog placements have no column (toColumnName null): the
      // sprint clause already says "(backlog)", so phrase around it
      if (columnChanged && !data.toColumnName) {
        return epicChanged
          ? `${actor} moved this card to the backlog in epic "${data.toEpicName}"${because}`
          : `${actor} moved this card to the backlog${because}`;
      }
      if (columnChanged && epicChanged) {
        return `${actor} moved this card to ${data.toColumnName} in epic "${data.toEpicName}"${sprint}${because}`;
      }
      if (columnChanged) {
        return `${actor} moved this card to ${data.toColumnName}${sprint}${because}`;
      }
      if (epicChanged) {
        return `${actor} moved this card to epic "${data.toEpicName}"${sprint}${because}`;
      }
      return `${actor} moved this card${sprint}${because}`;
    }
    case 'card_moved_project': {
      const destination = [
        data.toColumnName,
        data.toEpicName ? `epic "${data.toEpicName}"` : null,
        data.toSprintName && data.toSprintName !== 'Backlog'
          ? `sprint ${data.toSprintName}`
          : null,
      ]
        .filter(Boolean)
        .join(', ');
      const subject = data.cardTitle ? `"${data.cardTitle}"` : 'this card';
      return `${actor} moved ${subject} from ${data.fromProjectName} to ${data.toProjectName}${
        destination ? ` (${destination})` : ''
      }`;
    }
    case 'card_number_changed':
      return `Card id changed from ${data.fromRef} to ${data.toRef}`;
    case 'card_archived':
      return `${actor} archived the card${data.title ? ` "${data.title}"` : ''}`;
    case 'card_restored':
      return `${actor} restored the card${data.title ? ` "${data.title}"` : ''}`;
    case 'sprint_created':
      return `${actor} added sprint "${data.name || ''}"`;
    case 'sprint_updated':
      return `${actor} updated sprint "${data.name || ''}"`;
    case 'sprint_archived':
      return `${actor} archived sprint "${data.name || ''}" (its cards stay on it)`;
    case 'sprint_restored':
      return `${actor} restored sprint "${data.name || ''}" from the archive`;
    case 'sprint_deleted':
      return `${actor} permanently deleted sprint "${data.name || ''}" (its cards moved to the backlog)`;
    case 'sprint_epic_removed':
      return `${actor} removed epic "${data.epicName || ''}" from sprint "${data.sprintName || ''}"${
        data.movedCardsToEpicName
          ? ` (cards moved to "${data.movedCardsToEpicName}")`
          : ''
      }`;
    case 'epic_created':
      return `${actor} added epic "${data.name || ''}"`;
    case 'epic_updated':
      return `${actor} updated epic "${data.name || ''}"`;
    case 'epic_archived':
      return `${actor} archived epic "${data.name || ''}" and its cards`;
    case 'epic_restored':
      return `${actor} restored epic "${data.name || ''}" and its cards`;
    case 'epic_deleted':
      return `${actor} permanently deleted epic "${data.name || ''}" and its archived cards`;
    case 'card_deleted':
      return `${actor} deleted the card${data.title ? ` "${data.title}"` : ''}`;
    case 'comment_added':
      return `${actor} commented`;
    case 'comment_archived':
      return `${actor} archived a comment`;
    case 'comment_deleted':
      return `${actor} permanently deleted a comment`;
    case 'agent_job_started': {
      if (workStream && workStreamIdFromActivity(event) === workStream.id) {
        // Always "started", whatever the stream's current status. This entry
        // is stamped with the moment work began; rewriting it to "completed"
        // put the wrong word on the wrong timestamp and left the run's start
        // with no entry at all. The matching agent_job_finished row reports
        // how it ended, at the time it ended.
        return (
          <>
            {workStream.agentName || `Agent ${workStream.agentId}`} started a
            new{' '}
            <Link
              to={workStreamHref(teamId, {
                workStreamId: workStream.id,
              })}>
              work stream
            </Link>
          </>
        );
      }
      const fallback = (
        <>
          Agent work started
          {data.trigger ? ` (${data.trigger})` : ''}
        </>
      );
      return workStream === null ? (
        fallback
      ) : (
        <>
          {fallback} —{' '}
          <Link to={workStreamHref(teamId, data)}>view Work stream</Link>
        </>
      );
    }
    case 'agent_job_finished': {
      // Older finished rows carry no actorName. The work stream knows who ran
      // it, so prefer that over the generic "Agent" — otherwise a run reads
      // "Builder started a new work stream" then "Agent work completed", as
      // if two different actors were involved.
      const worker =
        event.actorName ||
        workStream?.agentName ||
        (workStream?.agentId ? `Agent ${workStream.agentId}` : 'Agent');
      // A stopped run reaches the job as a failure, so `data.status` alone
      // would call every human-cancelled stream "failed". The stream's own
      // terminal status is the truthful source when the card can see it —
      // this distinction used to live on the started row, which now only
      // ever says "started".
      const stopped =
        workStream?.status === 'stopped' ||
        workStream?.status === 'interrupted';
      const label = stopped
        ? `${worker} work stopped`
        : data.status === 'error'
          ? `${worker} work failed${data.error ? `: ${data.error}` : ''}`
          : `${worker} work completed`;
      const jobCost = formatCostCompact(data.costUsd);
      const ranFor = formatRunDuration(data.durationMs);
      const linked =
        data.workStreamId && workStream !== null ? (
          <Link
            to={workStreamHref(teamId, data)}
            data-testid={`agent-work-stream-link-${data.workStreamId}`}>
            {label}
          </Link>
        ) : (
          label
        );
      return jobCost || ranFor ? (
        <>
          {linked}
          {ranFor ? <> in {ranFor}</> : null}
          {jobCost ? (
            <span className="activity-cost" title="Cost of this agent run">
              {' '}
              · {jobCost}
            </span>
          ) : null}
        </>
      ) : (
        linked
      );
    }
    case 'agent_conversation_started':
      return (
        <>
          {actor} started a conversation with{' '}
          {data.peerAgentName || `agent ${data.peerAgentId}`}:{' '}
          {data.title || `conversation ${data.conversationId}`}
        </>
      );
    case 'task_added':
      return `${actor} added task "${data.title || ''}"`;
    case 'task_updated':
      return `${actor} updated a task`;
    case 'task_checked':
      return `${actor} ${data.done === false ? 'unchecked' : 'completed'} task "${data.title || ''}"`;
    case 'attachment_added':
      return (
        <>
          {actor} attached file{' '}
          <a
            href={`/api/project-card-attachments/${data.attachmentId}`}
            target="_blank"
            rel="noreferrer">
            {data.filename || 'file'}
          </a>
        </>
      );
    case 'attachment_reviewed':
      return (
        <>
          {actor} reviewed{' '}
          <a
            href={`/api/project-card-attachments/${data.attachmentId}`}
            target="_blank"
            rel="noreferrer">
            {data.filename || 'file'}
          </a>{' '}
          v{data.versionNumber || 1}: {data.decision || 'reviewed'}
          {data.note ? ` — ${data.note}` : ''}
        </>
      );
    case 'attachment_deleted':
      return `${actor} removed attachment "${data.filename || ''}"`;
    case 'member_added':
      return `${actor} added a member`;
    case 'member_removed':
      return `${actor} removed a member`;
    case 'agent_assigned':
      return `${actor} assigned agent ${data.agentName || data.agentId || ''}`;
    case 'agent_unassigned':
      return `${actor} unassigned an agent`;
    case 'label_created':
      return `${actor} created label "${data.name || ''}"`;
    case 'label_updated':
      return `${actor} updated a label`;
    case 'label_group_renamed':
      return data.newGroup
        ? `${actor} renamed label group "${data.group || ''}" to "${data.newGroup}" (${data.labelCount || 0} labels)`
        : `${actor} ungrouped the "${data.group || ''}" labels`;
    case 'label_archived':
      return `${actor} archived label "${data.name || ''}" (its cards keep it for restore)`;
    case 'label_restored':
      return `${actor} restored label "${data.name || ''}"`;
    case 'label_deleted':
      return `${actor} deleted label "${data.name || ''}"`;
    case 'column_created':
      return `${actor} added column "${data.name || ''}"`;
    case 'column_updated':
      return `${actor} updated a column`;
    case 'column_archived':
      return `${actor} archived column "${data.name || ''}"${
        data.movedCardsToColumnId ? ' (its cards moved)' : ''
      }`;
    case 'column_restored':
      return `${actor} restored column "${data.name || ''}"`;
    case 'column_deleted':
      return `${actor} deleted column "${data.name || ''}"`;
    default:
      return `${actor}: ${event.type}`;
  }
}

/**
 * One in-sentence substitution: swap `text[start, end)` for a component.
 *
 * `eventText` returns a plain sentence for most event types, and the pieces
 * of it a reader wants to click (the actor, the card) are already IN that
 * sentence. Splicing keeps the phrasing in exactly one place — the switch
 * above — instead of forcing every clickable part to be re-appended as a
 * second copy of text the sentence already contains.
 */
interface TextSplice {
  start: number;
  end: number;
  render: (text: string) => React.ReactNode;
}

/** Apply `splices` to `text`. Overlapping splices: the earlier one wins. */
function spliceText(text: string, splices: TextSplice[]): React.ReactNode {
  const ordered = splices
    .filter((splice) => splice.start >= 0 && splice.end > splice.start)
    .sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  ordered.forEach((splice, index) => {
    if (splice.start < cursor) return;
    if (splice.start > cursor) parts.push(text.slice(cursor, splice.start));
    parts.push(
      <React.Fragment key={`splice-${index}`}>
        {splice.render(text.slice(splice.start, splice.end))}
      </React.Fragment>,
    );
    cursor = splice.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

/** The actor's name where the sentence leads with it. */
function actorSplice(
  teamId: string,
  event: ProjectActivityEvent,
  text: string,
): TextSplice | null {
  const name = event.actorName;
  if (!name || !(event.actorUserId || event.actorAgentId)) return null;
  if (!text.startsWith(name)) return null;
  return {
    start: 0,
    end: name.length,
    render: (matched) => (
      <MemberName
        teamId={teamId}
        userId={event.actorUserId}
        agentId={event.actorAgentId}>
        {matched}
      </MemberName>
    ),
  };
}

/**
 * The card title where the sentence already quotes it ("created the card
 * \"Sensor Firmware\"").
 *
 * Three candidate titles because the sentence and the row disagree about
 * which one to use: `event.cardTitle` is the card's title NOW, while
 * `data.title` / `data.cardTitle` are the snapshot taken when the event
 * happened, and a renamed card makes them differ. Whichever one the
 * sentence actually contains is the one to linkify.
 */
function targetTitleSplice(
  text: string,
  event: ActivityEventWithTarget,
  target: ActivityTargetLink,
): TextSplice | null {
  const data = (event.data || {}) as Record<string, any>;
  const candidates = [event.cardTitle, data.cardTitle, data.title].filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
  for (const candidate of candidates) {
    const index = text.indexOf(`"${candidate}"`);
    if (index < 0) continue;
    return {
      // The quotes stay plain text; only the title itself is the link.
      start: index + 1,
      end: index + 1 + candidate.length,
      render: (matched) => (
        <Link
          to={target.path}
          onClick={target.onNavigate}
          data-testid={target.testId}>
          {matched}
        </Link>
      ),
    };
  }
  return null;
}

/** Team-scoped activity rows carry the target's current title. */
type ActivityEventWithTarget = ProjectActivityEvent & {
  cardTitle?: string;
  projectName?: string;
  projectKey?: string;
};

export interface ActivityTargetLink {
  /** Where the card (or, for project-level events, the project) lives. */
  path: string;
  /**
   * Link text for events whose sentence does NOT name the target — "updated
   * the card", "commented", or any project-level event. Those rows would
   * otherwise have no way to reach what they describe, so they keep the
   * trailing link.
   */
  label: string;
  onNavigate?: () => void;
  testId?: string;
}

/**
 * `eventText` with the actor's name as a click-target (member detail
 * modal for users, agent page for agents). Sentences that don't lead
 * with the actor's name (mostly link-bearing agent events) pass through
 * unchanged.
 */
export function eventTextWithActor(
  teamId: string,
  event: ProjectActivityEvent,
): React.ReactNode {
  const node = eventText(teamId, event);
  if (typeof node !== 'string') return node;
  const splice = actorSplice(teamId, event, node);
  return splice ? spliceText(node, [splice]) : node;
}

/**
 * `eventTextWithActor` plus a link to what the event happened ON, for feeds
 * that span more than one project and so have somewhere to send you.
 *
 * The title is linked IN PLACE where the sentence already quotes it, which
 * is the whole point: appending a trailing link instead printed the card
 * name twice ('created the card "QA pass" · QA pass'). Rows whose sentence
 * doesn't name the target — and rows whose text isn't a plain string
 * because `eventText` built its own links — fall back to the trailing
 * link, so every row stays reachable either way.
 */
export function eventTextWithTarget(
  teamId: string,
  event: ActivityEventWithTarget,
  target: ActivityTargetLink,
): React.ReactNode {
  const node = eventText(teamId, event);
  const trailing = (
    <>
      {' · '}
      <Link
        to={target.path}
        onClick={target.onNavigate}
        data-testid={target.testId}>
        {target.label}
      </Link>
    </>
  );
  if (typeof node !== 'string') {
    return (
      <>
        {node}
        {trailing}
      </>
    );
  }
  const titleSplice = targetTitleSplice(node, event, target);
  const splices = [actorSplice(teamId, event, node), titleSplice].filter(
    (splice): splice is TextSplice => splice !== null,
  );
  return (
    <>
      {spliceText(node, splices)}
      {titleSplice ? null : trailing}
    </>
  );
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ teamId, events }) => {
  if (events.length === 0) {
    return <p className="descriptionText">No activity yet.</p>;
  }
  return (
    <div className="activity-feed" data-testid="activity-feed">
      {events.map((event) => (
        <div
          key={event.id}
          className="activity-row"
          data-testid={`activity-${event.type}-${event.id}`}>
          <span className="activity-time">{formatTime(event.createdAt)}</span>
          <span className="activity-text">
            {eventTextWithActor(teamId, event)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ActivityFeed;
